import rpcHeaders from '#/store/headers';
import { queryUserClient, modifyUserClient, verifyUserClient } from '#/clients';
import { getInstance } from '#/auth';
import { UserGetDetailedInfoRequest, ListAccountInvitesRequest, ListAllSettingsRequest } from '#/protobuf/service_user_pb';

export function emptyState() {
  return {
    userDetails: undefined,
    emailVerified: false,
    mayTest: false,
    mayBeta: false,
    invites: [],
    workspaceIdToUserSettings: {},
  };
}

export const getters = {
  userDetails: (state) => state.userDetails,
  userPhoto: (state) => (state.userDetails ? state.userDetails.userPictureUrl : '/img/icons/account/account-circle.svg'),
  userNickname: (state) => (state.userDetails ? state.userDetails.userNickname : ''),
  userFirstName: (state) => (state.userDetails ? state.userDetails.userFirstName : ''),
  userLastName: (state) => (state.userDetails ? state.userDetails.userLastName : ''),
  userPhoneNumber: (state) => (state.userDetails ? state.userDetails.userPhoneNumber : ''),
  userEmail: (state) => (state.userDetails ? state.userDetails.userEmail : ''),
  userNeedsOnboard: (state) => (state.userDetails ? state.userDetails.userNeedsOnboarding : false), // TODO: should userNeedsOnboard default to true here?
  isEmailVerified: (state) => state.emailVerified,
  mayBeta: (state) => state.mayBeta,
  mayTest: (state) => state.mayTest,
  invites: (state) => state.invites,
  defaultProjectId: (state) => ((state.userDetails && state.userDetails.userDefaultProject) ? state.userDetails.userDefaultProject : undefined),
  settingsForWorkspaceId: (state) => (workspaceId) => state.workspaceIdToUserSettings[workspaceId],
};

export const mutations = {

  setTokenMeta: (state, token) => {
    if (typeof (token.email_verified) !== 'undefined' && token.email_verified) {
      state.emailVerified = true;
    }

    const mt = 'https://api.imagineproducts.com/may_test';
    state.mayTest = typeof (token[mt]) !== 'undefined' ? token[mt] : false;
    if (state.mayTest) {
      // log the bearer token if this is test cluster and may test.
      console.info('Token Meta:', token);
      // cuz for some reason the one i pull inside of this looks differnent than the meta
      getInstance().getTokenSilently().then((bearer) => console.info(bearer));
    }

    const mb = 'https://api.imagineproducts.com/may_beta';
    state.mayBeta = typeof (token[mb]) !== 'undefined' ? token[mb] : false;

    console.info('setTokenMeta', 'state.mayBeta:', state.mayBeta, 'state.mayTest:', state.mayBeta);
  },
  setUser: (state, user) => {
    state.userDetails = user;
    console.info('set user', state.userDetails);
  },
  setInvites: (state, invites) => {
    state.invites.length = 0;
    state.invites.push(...invites);
    console.log('set invites', state.invites);
  },
  setWorkspaceSettings: (state, { workspaceId, settings }) => {
    state.workspaceIdToUserSettings[workspaceId] = settings;
    console.info('committed workspace settings', workspaceId, settings);
  },
};

export const actions = {
  loadTokenFlags: async ({ commit }) => {
    getInstance().getIdTokenClaims().then((result) => {
      commit('setTokenMeta', result);
      Promise.resolve(result);
    });
  },
  loadDetails: async ({ rootState, commit }) => {
    console.log('loading user getDetailedInfo...');
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        const request = new UserGetDetailedInfoRequest();
        queryUserClient.getDetailedInfo(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from user getDetailedInfo', err);
            return reject(err);
          }
          const r = response.toObject();
          commit('setUser', r.info);
          return resolve(r);
        });
      });
    } catch (error) {
      console.log('caught error loading user details:', error);
      return Promise.reject(error);
    }
  },
  loadPendingInvites: async ({ rootState, commit }) => {
    console.log('loading user pending invites...');
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        const request = new ListAccountInvitesRequest();
        queryUserClient.listAccountInvites(request, headers, (err, response) => {
          if (err != null) {
            console.warn('received err from user loadPendingInvites', err);
            return reject(err);
          }
          const r = response.toObject();
          commit('setInvites', r.pendingInvitesList);
          return resolve(r);
        });
      });
    } catch (error) {
      console.warn('caught error loading user pending invites:', error);
      return Promise.reject(error);
    }
  },
  updateDetails: async ({ rootState }, request) => {
    // TODO: update the store record with the response details once dto is updated.
    console.log('updating user profile', request.toObject());
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        modifyUserClient.updateDetailedInfo(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from user profile update', err);
            return reject(err);
          }
          return resolve(response);
        });
      });
    } catch (error) {
      console.log('caught error in user profile update', error);
      return Promise.reject(error);
    }
  },
  updateUserAccountSettings: async ({ rootState }, request) => {
    console.log('updating user account settings', request.toObject());
    try {
      const headers = await rpcHeaders(rootState, true, true);
      return new Promise((resolve, reject) => {
        modifyUserClient.updateUserAccountSettings(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from updateUserAccountSettings', err);
            return reject(err);
          }
          return resolve(response);
        });
      });
    } catch (error) {
      console.log('caught error in updateUserAccountSettings', error);
      return Promise.reject(error);
    }
  },
  resolveInvite: async ({ rootState }, request) => {
    console.log('resolving user invite', request.toObject());
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        modifyUserClient.resolveUserInvite(request, headers, (err, response) => {
          if (err != null) {
            console.error('received err from resolveUserInvite', err);
            return reject(err);
          }
          const r = response.toObject();
          console.log('received response from resolveUserInvite', r);
          return resolve(r);
        });
      });
    } catch (error) {
      console.error('caught error in resolveUserInvite', error);
      return Promise.reject(error);
    }
  },
  resendVerificationEmail: async ({ rootState }, request) => {
    console.log('attempting to resend verification email', request.toObject());
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        verifyUserClient.requestResendOfVerificationEmail(request, headers, (err, response) => {
          if (err != null) {
            console.error('received err from requestResendOfVerificationEmail', err);
            return reject(err);
          }
          const r = response.toObject();
          console.log('received response from requestResendOfVerificationEmail', r);
          return resolve(r);
        });
      });
    } catch (error) {
      console.error('caught error in requestResendOfVerificationEmail', error);
      return Promise.reject(error);
    }
  },
  requestProfilePicUpload: async ({ rootState }, request) => {
    console.log('attempting to get credentials to upload profile pic', request.toObject());
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        modifyUserClient.getProfileImageUpload(request, headers, (err, response) => {
          if (err != null) {
            console.error('received err from getProfileImageUpload', err);
            return reject(err);
          }
          const r = response;
          console.log('received response from getProfileImageUpload', r.toObject());
          return resolve(r);
        });
      });
    } catch (error) {
      console.error('caught error in getProfileImageUpload', error);
      return Promise.reject(error);
    }
  },
  loadWorkspaceUserSettings: async ({ rootState, commit }) => {
    console.log('loading user workspace settings...');
    try {
      const headers = await rpcHeaders(rootState, false, false);
      return new Promise((resolve, reject) => {
        const request = new ListAllSettingsRequest();
        queryUserClient.listAllSettings(request, headers, (err, response) => {
          if (err != null) {
            console.warn('received err from user listAllSettings', err);
            return reject(err);
          }
          console.info('loaded user workspace settings', response.toObject());
          // iterate and commit our settings for each workspace to the store
          const settingsMap = response.getAccountSettingsMap();
          // note the value and key are flipped here in protobuf maps
          settingsMap.forEach((workspaceSettings, workspaceId) => {
            // here we "toObject" the settings val
            const settings = workspaceSettings.toObject();
            // commit to store state
            commit('setWorkspaceSettings', { workspaceId, settings });
          });

          return resolve(response);
        });
      });
    } catch (error) {
      console.warn('caught error loading user listAllSettings:', error);
      return Promise.reject(error);
    }
  },
};

export default {
  namespaced: true,
  state: emptyState(),
  getters,
  mutations,
  actions,
};
